import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clapperboard, Play, ArrowRight, Eye } from "lucide-react";

const GENRE_LABEL = {
  nollywood: "Nollywood", drama: "Drama", comedy: "Comedy", action: "Action",
  romance: "Romance", thriller: "Thriller", epic: "Epic", documentary: "Documentary",
  kids: "Kids", music: "Music", other: "Other",
};

// Homepage "Now Showing" cinematic strip. Shows the newest movie trailers once
// there are 3+, otherwise an inviting "premiere your film" CTA so the section
// is always visible and recruiting filmmakers.
const NowShowing = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/movies?sort=featured&limit=12")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const hasRail = loaded && items.length >= 3;

  return (
    <section className="relative overflow-hidden bg-[#0B0A0F] text-white" data-testid="now-showing-section">
      <div
        className="absolute inset-0 pointer-events-none opacity-90"
        style={{
          background:
            "radial-gradient(circle at 90% 0%, #E4B85122 0%, transparent 40%), radial-gradient(circle at 0% 100%, #B01E2822 0%, transparent 45%)",
        }}
      />
      <div className="relative container-narrow py-16 sm:py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] font-semibold text-[#E4B851] mb-2 flex items-center gap-2">
              <Clapperboard className="w-3.5 h-3.5" /> Now Showing
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Nollywood &amp; African cinema, on demand.
            </h2>
            <p className="text-white/60 mt-2 max-w-xl font-sans-ui leading-relaxed">
              Watch the latest trailers, discover independent filmmakers, and jump straight
              to stream, rent or buy the full film. Made a movie? Premiere it to the diaspora.
            </p>
          </div>
          <Link
            to="/movies/new"
            data-testid="now-showing-submit-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#0B0A0F] hover:opacity-95 transition-opacity shrink-0 font-semibold text-sm"
            style={{ background: "linear-gradient(120deg, #E4B851 0%, #F26D5B 100%)" }}
          >
            <Play className="w-4 h-4" /> Premiere your film
          </Link>
        </div>

        {hasRail ? (
          <div
            className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory"
            data-testid="now-showing-rail"
          >
            {items.map((m) => (
              <Link
                key={m.movie_id}
                to={`/movies/${m.movie_id}`}
                data-testid={`now-showing-${m.movie_id}`}
                className="snap-start flex-shrink-0 w-[170px] sm:w-[190px] group rounded-xl overflow-hidden bg-white/[0.04] border border-white/10 hover:border-[#E4B851]/60 transition-colors"
              >
                <div className="relative aspect-[2/3] bg-white/5 overflow-hidden">
                  {m.poster_url ? (
                    <img
                      src={m.poster_url}
                      alt={m.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <Clapperboard className="w-9 h-9" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold uppercase tracking-wider text-[#E4B851]">
                    {GENRE_LABEL[m.genre] || m.genre}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-5 h-5 text-[#0B0A0F] fill-[#0B0A0F] ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm line-clamp-1">{m.title}</div>
                  <div className="text-[11px] text-white/50 mt-1 flex items-center gap-2">
                    <span>{m.country}</span>
                    {m.year ? <span>· {m.year}</span> : null}
                    <span className="inline-flex items-center gap-0.5 ml-auto">
                      <Eye className="w-3 h-3" /> {m.view_count}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            <Link
              to="/movies"
              data-testid="now-showing-tail-cta"
              className="snap-start flex-shrink-0 w-[150px] rounded-xl border-2 border-dashed border-[#E4B851]/40 flex flex-col items-center justify-center text-center p-6 hover:border-[#E4B851] hover:bg-white/5 transition-colors"
            >
              <Clapperboard className="w-6 h-6 text-[#E4B851] mb-2" />
              <div className="font-semibold text-sm">See all movies</div>
              <div className="text-xs text-white/50 mt-1">Browse by genre →</div>
            </Link>
          </div>
        ) : (
          <div
            className="rounded-2xl border-2 border-dashed border-[#E4B851]/30 bg-white/[0.03] p-8 sm:p-10 text-center"
            data-testid="now-showing-invite"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E4B851]/10 mb-3">
              <Clapperboard className="w-6 h-6 text-[#E4B851]" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight">Premiere the next big African film.</h3>
            <p className="text-white/60 mt-2 max-w-lg mx-auto font-sans-ui">
              Drop your trailer, add a poster, and tell audiences where to watch, rent or buy —
              from YouTube to Netflix and beyond. Get your movie in front of the diaspora.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/movies/new"
                data-testid="now-showing-invite-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[#0B0A0F] font-semibold text-sm hover:opacity-95 transition-opacity"
                style={{ background: "linear-gradient(120deg, #E4B851 0%, #F26D5B 100%)" }}
              >
                <Play className="w-4 h-4" /> Premiere your film
              </Link>
              <Link
                to="/movies"
                data-testid="now-showing-invite-explore"
                className="inline-flex items-center gap-1 text-sm font-medium text-white/80 hover:text-[#E4B851] transition-colors"
              >
                Explore movies <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NowShowing;
